import React, { FC, useState } from "react";
import { useToggleState } from "repoV2/utils/common/dataTypes/boolean";
import { classNames } from "repoV2/utils/common/render/classNames";
import {
    checkIsYTVidUrl,
    isLoomVideoUrl,
} from "repoV2/features/Common/modules/URLsNRouting/URLsNRouting.utils";
import { useOptimizationBasedLightProp } from "repoV2/components/common/VideoPlayer/VideoPlayer.utils";
import { ADD_VIDEO_OPTIONS } from "../../constants";
import VideoPlayer, { IVideoPlayerProps } from "../common/VideoPlayer";
import styles from "./EmbedNUrlVideoPlayer.module.scss";
import PlayerWrapper from "./modules/PlayerWrapper/PlayerWrapper";

interface IEmbedNUrlVideoPlayerProps extends IVideoPlayerProps {
    videoSrcType: string;
    wrapperClassName?: string;
    lightImgFetchWidth?: number;
}

const EmbedNUrlVideoPlayer: FC<IEmbedNUrlVideoPlayerProps> = ({
    url,
    controls,
    playIcon,
    style,
    className,
    parentRef,
    autoPlay,
    volume,
    isMuted,
    loop,
    light,
    fitParent,
    playsinline,
    videoSrcType,
    wrapperClassName,
    showPlayIcon = true,
    optimizeUsingThumbnail = true,
    lightImgFetchWidth,
}) => {
    const enableLightMode = light || !autoPlay;
    const { lightProp } = useOptimizationBasedLightProp({
        mediaUrl: url as string,
        light: enableLightMode, // light
        optimizeUsingThumbnail,
        lightImgFetchWidth,
    });
    const [isPlaying, startPlaying, stopPlaying, togglePlaying] =
        useToggleState(!!autoPlay);

    // Whether video ever started or not.
    // used to manipulate PlayerWrapper props when lightProp is true
    const [isVideoStarted, setIsVideoStarted] = useState(!!autoPlay);

    const [isMutedControlledState, , unmute] = useToggleState(
        !!(isMuted || autoPlay)
    );

    if (url && videoSrcType === ADD_VIDEO_OPTIONS.ADD_EMBED_CODE.slug) {
        return (
            <div
                className={classNames(styles.embedRoot, className)}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: url,
                }}
            />
        );
    }

    const isLoomVideo = isLoomVideoUrl(url);
    const isYouTubeVideo = !!(url && checkIsYTVidUrl(url));
    const showUnmuteCta =
        !controls && autoPlay && isMutedControlledState && !isLoomVideo; // Note: The Loom video player currently lacks support for custom controls

    /**
     * explanation for `(lightProp ? isVideoStarted : true)` in `showControlsLayer` and `showPlayCta`
     *
     * if: lightProp exists
     * then:
     *       if: !isVideoStarted
     *       then: we dont show the PlayerWrapper UI and let the react player handle it
     *       why: if we show the PlayerWrapper UI as well, the user will have to click twice to play the video
     *
     *       else: isVideoStarted
     *       then: we let PlayerWrapper UI takeover
     *
     * else: we let the default condition take over which is show controls layer if video controls are disabled from creator tool. Explanation:
     *
     * Purpose of showControlsLayer:
     *  The controls layer is an overlay on the video player that allows us to toggle play/pause.
     *  This is needed when player controls are disabled from the creator tool,
     *  and in that case, the user is unable to toggle play/pause.
     *  Hence, we are showing this layer.
     *
     * Purpose of showPlayCta: showPlayCta determines the visiblity of play icon cta in paused state
     */
    const showControlsLayer =
        (lightProp ? isVideoStarted : true) && !controls && !isLoomVideo;
    const showPlayCta =
        (lightProp ? isVideoStarted : true) &&
        !controls &&
        !isPlaying &&
        showPlayIcon;

    const onPlayerWrapperPlayClick = () => {
        // unmute only on click if video is autoplaying on mute
        if (showUnmuteCta) unmute();

        //
        // toggle isPlaying state only if showControlsLayer or showPlayCta is true
        // why: strangely, even if both showControlsLayer and showPlayCta are false,
        //      the PlayerWrapper still triggers the onPlayerWrapperPlayClick function
        if (showControlsLayer || showPlayCta) togglePlaying();
    };

    const onVideoPlayerPlayClick = () => {
        if (!isVideoStarted) setIsVideoStarted(true);
        startPlaying();
    };

    return (
        <PlayerWrapper
            showPlayCta={showPlayCta}
            showUnmuteCta={showUnmuteCta}
            showControlsLayer={showControlsLayer}
            isYouTubeVideo={isYouTubeVideo}
            onClickPlay={onPlayerWrapperPlayClick}
            onClickUnmute={unmute}
            className={wrapperClassName}
        >
            <VideoPlayer
                playing={isPlaying}
                url={url}
                controls={controls}
                playIcon={playIcon}
                style={style}
                className={classNames(styles.root, className)}
                parentRef={parentRef}
                autoPlay={autoPlay}
                volume={volume}
                isMuted={isMutedControlledState}
                loop={loop}
                light={lightProp}
                fitParent={fitParent}
                playsinline={playsinline}
                width="100%"
                height="100%"
                onEnded={stopPlaying}
                onPlay={onVideoPlayerPlayClick}
            />
        </PlayerWrapper>
    );
};

export default EmbedNUrlVideoPlayer;
